import { ReactNode } from "react";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { formatNumber } from "../../../../utils";
import { GROUP_RADIUS } from "../constants";
import { PriceChangeNote, PriceChangeRow } from "./priceChangeRows";

/** Room for a product name beside the two price pairs without the dialog reflowing per row. */
const CHANGE_LIST_MIN_WIDTH = 560;
const PRICE_PAIR_WIDTH = 150;
const NO_PRICE = "—";

const NOTE_LABEL: Record<PriceChangeNote, string> = {
  added: "เพิ่มใหม่",
  removed: "นำออก",
  enabled: "เปิดขาย",
  disabled: "ปิดขาย",
};

interface PriceChangeListProps {
  rows: PriceChangeRow[];
  /** Shown instead of the rows when there are none. */
  emptyText: string;
}

/**
 * Each changed line with what it bills at now, crossed out, and what it will bill at — incl
 * and ex VAT. Shared by the price-save confirmation and the draft apply dialog.
 */
export default function PriceChangeList({ rows, emptyText }: PriceChangeListProps) {
  return (
    <Stack
      sx={{
        minWidth: { sm: CHANGE_LIST_MIN_WIDTH },
        border: (theme) => `1px solid ${theme.palette.draft.main}`,
        borderRadius: GROUP_RADIUS,
      }}
    >
      <ChangeLine name="สินค้า" inclVat="รวม VAT" exVat="ก่อน VAT" isHeader />
      {rows.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1 }}>
          {emptyText}
        </Typography>
      )}
      {rows.map((row) => (
        <ChangeLine
          key={row.productCode}
          name={row.productName}
          note={row.note}
          inclVat={<PricePair before={row.beforeInclVat} after={row.afterInclVat} />}
          exVat={<PricePair before={row.beforeExVat} after={row.afterExVat} />}
        />
      ))}
    </Stack>
  );
}

interface ChangeLineProps {
  name: string;
  note?: PriceChangeNote | null;
  inclVat: ReactNode;
  exVat: ReactNode;
  isHeader?: boolean;
}

function ChangeLine({ name, note, inclVat, exVat, isHeader = false }: ChangeLineProps) {
  const textColor = isHeader ? "text.secondary" : "text.primary";

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      sx={{
        px: 1.5,
        py: 1,
        borderBottom: (theme) => `1px solid ${theme.palette.draft.main}`,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Stack direction="row" alignItems="center" gap={1} sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" color={textColor} noWrap>
          {name}
        </Typography>
        {note && <Chip label={NOTE_LABEL[note]} size="small" variant="outlined" />}
      </Stack>
      <Typography
        component="div"
        variant="body2"
        color={textColor}
        align="right"
        sx={{ width: PRICE_PAIR_WIDTH, flexShrink: 0 }}
      >
        {inclVat}
      </Typography>
      <Typography
        component="div"
        variant="body2"
        color={textColor}
        align="right"
        sx={{ width: PRICE_PAIR_WIDTH, flexShrink: 0 }}
      >
        {exVat}
      </Typography>
    </Stack>
  );
}

interface PricePairProps {
  before: number | null;
  after: number | null;
}

/** A price struck through, an arrow, and the price it becomes; also used by the face-price dialog. */
export function PricePair({ before, after }: PricePairProps) {
  return (
    <Stack direction="row" justifyContent="flex-end" gap={0.75}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textDecoration: "line-through" }}
      >
        {formatNumber(before, { fallback: NO_PRICE })}
      </Typography>
      <Typography variant="body2" color="text.disabled">
        →
      </Typography>
      <Typography variant="body2" color="warning.main" fontWeight={600}>
        {formatNumber(after, { fallback: NO_PRICE })}
      </Typography>
    </Stack>
  );
}
